<template>
  <button
    type="button"
    class="vs-overlay"
    :class="{ clash, fallback, compact: duelCompact }"
    @click="skip"
  >
    <div ref="host" class="vs-stage" aria-hidden="true" />
    <div class="letterbox top" />
    <div class="letterbox bot" />
    <div class="vignette" />
    <div class="scan" />
    <div class="flash" />

    <p class="kicker">{{ t('duel.vs.kicker') }}</p>

    <div v-if="fallback" class="fallback-fighters">
      <div class="fighter you">
        <AvatarView :config="leftAvatar" :size="avatarSize" />
      </div>
      <div class="fighter them">
        <AvatarView :config="rightAvatar" :size="avatarSize" />
      </div>
    </div>

    <div class="vs-mark" aria-hidden="true">
      <span class="vs-letter">V</span>
      <span class="vs-letter">S</span>
    </div>

    <div class="names">
      <p class="name left">{{ leftName }}</p>
      <p class="name right">{{ rightName }}</p>
    </div>

    <p class="skip-hint">{{ t('duel.vs.skip') }}</p>
  </button>
</template>

<script setup lang="ts">
import { computed, onMounted, onUnmounted, ref } from 'vue'
import { VS_INTRO_MS, parseAvatarConfig, type AvatarConfig } from '@shared/index'
import { useI18n } from '../i18n/t'
import { useCompactLayout } from '../composables/useCompactLayout'
import { DuelSfx } from '../game/audio/DuelSfx'
import { VsArena } from '../game/vs/VsArena'
import AvatarView from './AvatarView.vue'

const props = defineProps<{
  leftName: string
  rightName: string
  leftAvatar: AvatarConfig
  rightAvatar: AvatarConfig
}>()

const emit = defineEmits<{
  done: []
}>()

const { t } = useI18n()
const { duelCompact } = useCompactLayout()
const avatarSize = computed(() => (duelCompact.value ? 160 : 240))
const host = ref<HTMLElement | null>(null)
const clash = ref(false)
const fallback = ref(false)

let arena: VsArena | null = null
let timer: ReturnType<typeof setTimeout> | null = null
let finished = false

function finish(): void {
  if (finished) return
  finished = true
  if (timer) {
    clearTimeout(timer)
    timer = null
  }
  emit('done')
}

function skip(): void {
  finish()
}

function onImpact(): void {
  clash.value = true
  DuelSfx.play('impact')
  DuelSfx.play('summon')
}

onMounted(() => {
  DuelSfx.play('packWhoosh')
  try {
    if (host.value) {
      arena = new VsArena(host.value, {
        left: parseAvatarConfig(props.leftAvatar),
        right: parseAvatarConfig(props.rightAvatar),
        onImpact,
        onContextLost: () => {
          window.setTimeout(() => {
            fallback.value = true
            clash.value = true
            arena?.dispose()
            arena = null
          }, 0)
        },
      })
    } else {
      fallback.value = true
      clash.value = true
    }
  } catch {
    fallback.value = true
    clash.value = true
  }
  timer = setTimeout(finish, VS_INTRO_MS)
})

onUnmounted(() => {
  if (timer) clearTimeout(timer)
  arena?.dispose()
  arena = null
})
</script>

<style scoped>
.vs-overlay {
  position: fixed;
  inset: 0;
  z-index: 520;
  margin: 0;
  padding: 0;
  border: 0;
  overflow: hidden;
  cursor: pointer;
  color: #f4f0ea;
  font: inherit;
  background:
    radial-gradient(ellipse at 18% 48%, rgba(20, 80, 90, 0.45), transparent 52%),
    radial-gradient(ellipse at 82% 48%, rgba(120, 24, 32, 0.42), transparent 52%),
    linear-gradient(180deg, #05060c 0%, #101018 48%, #05060c 100%);
}

.vs-stage {
  position: absolute;
  inset: 0;
}

.letterbox {
  position: absolute;
  left: 0;
  right: 0;
  height: 5vh;
  z-index: 2;
  pointer-events: none;
  background: #000;
}

.letterbox.top {
  top: 0;
  box-shadow: 0 18px 40px rgba(0, 0, 0, 0.45);
}

.letterbox.bot {
  bottom: 0;
  box-shadow: 0 -18px 40px rgba(0, 0, 0, 0.45);
}

.vignette {
  position: absolute;
  inset: 0;
  z-index: 2;
  pointer-events: none;
  background: radial-gradient(
    ellipse at 50% 48%,
    transparent 42%,
    rgba(0, 0, 0, 0.55) 100%
  );
}

.scan {
  position: absolute;
  inset: 0;
  z-index: 2;
  pointer-events: none;
  opacity: 0.18;
  background-image: repeating-linear-gradient(
    180deg,
    transparent,
    transparent 2px,
    rgba(0, 0, 0, 0.35) 3px
  );
}

.flash {
  position: absolute;
  inset: 0;
  z-index: 3;
  pointer-events: none;
  opacity: 0;
  background: radial-gradient(
    circle at 50% 48%,
    rgba(255, 248, 220, 0.8),
    rgba(232, 197, 71, 0.2) 38%,
    transparent 62%
  );
}

.clash .flash {
  animation: vs-flash 0.38s ease-out;
}

.kicker {
  position: absolute;
  top: calc(5vh + 12px);
  left: 0;
  right: 0;
  z-index: 4;
  margin: 0;
  font-size: 12px;
  font-weight: 700;
  letter-spacing: 0.42em;
  text-transform: uppercase;
  color: #fde68a;
  text-shadow: 0 0 18px rgba(232, 197, 71, 0.55);
  animation: kicker-in 0.7s ease-out both;
}

.fallback-fighters {
  position: absolute;
  inset: 18% 8% 22%;
  z-index: 1;
  display: flex;
  justify-content: space-between;
  align-items: center;
  pointer-events: none;
}

.fighter {
  display: flex;
  justify-content: center;
}

.fallback .vs-stage {
  display: none;
}

.vs-mark::before {
  content: '';
  position: absolute;
  left: -38%;
  right: -38%;
  top: 52%;
  height: 3px;
  background: linear-gradient(
    90deg,
    transparent,
    rgba(232, 197, 71, 0.95),
    transparent
  );
  transform: translateY(-50%);
  z-index: -1;
}

.vs-mark {
  position: absolute;
  left: 50%;
  top: 50%;
  z-index: 4;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 0.08em;
  padding: 0.22em 0.28em 0.28em;
  font-size: clamp(64px, 13vw, 128px);
  font-weight: 900;
  font-style: italic;
  letter-spacing: -0.04em;
  line-height: 1.28;
  transform: translate(-50%, -50%) perspective(640px) rotateX(8deg);
  transform-style: preserve-3d;
  overflow: visible;
  opacity: 0;
  pointer-events: none;
}

.clash .vs-mark {
  animation: vs-slam 0.55s cubic-bezier(0.16, 1.28, 0.36, 1) both;
}

.vs-letter {
  display: inline-block;
  /* Italic glyphs lean past the span box; the gold gradient only paints
     inside it. Extra horizontal padding covers the tip; negative margin
     keeps the letters where they were. */
  padding: 0.12em 0.3em 0.12em 0.16em;
  margin: 0 -0.22em 0 -0.08em;
  background: linear-gradient(180deg, #fff8d4 0%, #e8c547 42%, #8a6410 100%);
  -webkit-background-clip: text;
  background-clip: text;
  color: transparent;
  filter:
    drop-shadow(0 0 22px rgba(232, 197, 71, 0.9))
    drop-shadow(0 14px 22px rgba(0, 0, 0, 0.7));
}

.vs-letter:first-child {
  transform: rotateY(-10deg) translateZ(16px);
}

.vs-letter:last-child {
  transform: rotateY(10deg) translateZ(16px);
}

.names {
  position: absolute;
  left: 6%;
  right: 6%;
  bottom: calc(5vh + 32px);
  z-index: 4;
  display: flex;
  justify-content: space-between;
  gap: 16px;
  pointer-events: none;
}

.name {
  margin: 0;
  max-width: 42%;
  font-size: clamp(18px, 3.2vw, 34px);
  font-weight: 800;
  letter-spacing: 0.14em;
  text-transform: uppercase;
  opacity: 0;
}

.name.left {
  color: #99f6e4;
  text-align: left;
  text-shadow:
    0 0 18px rgba(45, 212, 191, 0.55),
    0 3px 14px rgba(0, 0, 0, 0.85);
}

.name.right {
  color: #fca5a5;
  text-align: right;
  text-shadow:
    0 0 18px rgba(248, 113, 113, 0.55),
    0 3px 14px rgba(0, 0, 0, 0.85);
}

.clash .name.left {
  animation: name-left 0.5s 0.05s cubic-bezier(0.22, 1, 0.36, 1) both;
}

.clash .name.right {
  animation: name-right 0.5s 0.05s cubic-bezier(0.22, 1, 0.36, 1) both;
}

.skip-hint {
  position: absolute;
  left: 0;
  right: 0;
  bottom: calc(5vh + 8px);
  z-index: 4;
  margin: 0;
  font-size: 11px;
  letter-spacing: 0.2em;
  text-transform: uppercase;
  color: #c6c5d0;
  opacity: 0.75;
}

.compact .kicker {
  letter-spacing: 0.28em;
  font-size: 11px;
}

.compact .vs-mark {
  font-size: clamp(48px, 12vw, 84px);
  top: 50%;
  line-height: 1.3;
  padding: 0.24em 0.3em 0.3em;
}

.compact .names {
  bottom: calc(5vh + 24px);
}

.compact .name {
  letter-spacing: 0.08em;
  font-size: clamp(14px, 4.2vw, 22px);
}

@keyframes kicker-in {
  from {
    opacity: 0;
    letter-spacing: 0.12em;
  }
  to {
    opacity: 1;
  }
}

@keyframes vs-slam {
  0% {
    opacity: 0;
    transform: translate(-50%, -50%) perspective(640px) rotateX(18deg) scale(1.08);
  }
  62% {
    opacity: 1;
  }
  100% {
    opacity: 1;
    transform: translate(-50%, -50%) perspective(640px) rotateX(8deg) scale(1);
  }
}

@keyframes vs-flash {
  0% {
    opacity: 1;
  }
  100% {
    opacity: 0;
  }
}

@keyframes name-left {
  from {
    opacity: 0;
    transform: translateX(-28px);
  }
  to {
    opacity: 1;
    transform: none;
  }
}

@keyframes name-right {
  from {
    opacity: 0;
    transform: translateX(28px);
  }
  to {
    opacity: 1;
    transform: none;
  }
}

@media (prefers-reduced-motion: reduce) {
  .kicker,
  .clash .vs-mark,
  .clash .name.left,
  .clash .name.right,
  .clash .flash {
    animation: none;
    opacity: 1;
    transform: none;
  }

  .clash .vs-mark {
    transform: translate(-50%, -50%) perspective(640px) rotateX(8deg);
  }
}
</style>
